// components/tour/tourSteps.js
//
// Declarative tour content. buildTracks({ staffId, isSuperUser }) returns the
// role-filtered, route-resolved tracks for the chooser + runner. Step shape:
//   { id, roles:['all'|'manager'|'super'], route?, target?, fallback?,
//     extraTarget?, column?, drive?, title, body }
// Selectors are existing stable classes or data-tour="…" attributes added in the
// page JSX. `:me` in a route is replaced with the signed-in user's staffId.
// `drive` asks the active page to open a modal/panel on the seeded demo data.
//
// Content authored from the RPRS user guide + 2026-06-29 training transcript
// (scheduling_brainstorm/tour materials).

const GRID = '/schedule/grid?view=people&dept=mine'; // tour pins People view + My Schedule so highlights/scroll aren't thrashed by All/Jobs volume
const SENTINEL_JOB = -1; // tourDemoData job id — StaffSuggestions/Manager demo branches key on it

// ── Getting Started: short cross-page essentials ────────────────────────────
const GETTING_STARTED = [
  {
    id: 'gs-welcome', roles: ['all'], route: GRID,
    title: 'Welcome to RPRS scheduling',
    body: "This is your home for scheduling. We'll take ~2 minutes to show the essentials — you can press End anytime.",
  },
  {
    id: 'gs-yourschedule', roles: ['all'], route: GRID,
    target: '[data-tour="my-row"]', fallback: '.sched-grid-root',
    title: 'This is your schedule',
    body: "The grid opens on your own week — this is your row. Each block is work you're scheduled on. Anything that affects your availability should live here.",
  },
  {
    id: 'gs-add', roles: ['all'], route: GRID, drive: 'grid-new-entry',
    target: '.sched-mgr-modal', placement: 'side', autoScroll: '.sched-mgr-modal',
    fallback: '.sched-grid-root',
    title: 'Add an entry — with honest hours',
    body: "Click a day in your row (or + Schedule) to open this form, pick a job, and enter your TRUE total hours — if you have 60 hours of work, put 60. That's how we see who's overloaded. The Before/After view shows the impact on your week.",
  },
  {
    id: 'gs-modes', roles: ['all'], route: GRID, drive: 'grid-new-entry',
    target: '[data-tour="dist-modes"]', fallback: '.sched-mgr-modal-backdrop',
    title: 'Spread the hours your way',
    body: 'Total spreads evenly; By Day lets you set each day; Optimize fills open capacity; Fill mode uses all that’s left; Extend overflows into the next week when one week isn’t enough.',
  },
  {
    id: 'gs-drag', roles: ['all'], route: GRID,
    target: '[data-tour="demo-block"]', fallback: '.sched-grid-root',
    title: 'Move work by dragging',
    body: 'Drag any block to another day — or onto another person — to move or reassign it. You’ll get a preview before anything commits.',
  },
  {
    id: 'gs-notif', roles: ['all'], route: GRID,
    target: '.app-notif-bell',
    title: 'Notifications keep you in the loop',
    body: "When your schedule changes — by you or the production team — you get a notification here. No more mystery about what moved.",
  },
  {
    id: 'gs-help', roles: ['all'], route: GRID,
    target: '.app-header-actions',
    title: 'Help, bugs & ideas',
    body: 'Up here: 💡 suggest an improvement, 🐛 report a bug (you’ll get notified when it’s addressed), and ? to replay this tour. The KB Navigator (bottom-right) can also point you anywhere.',
  },
  {
    id: 'gs-backlog', roles: ['all'], route: '/schedule/dashboard?tab=backlog',
    target: '[data-tour="dash-tab-backlog"]', fallback: '.sched-tabs',
    title: 'Check your backlog weekly',
    body: 'This is the My Backlog tab — it surfaces jobs you’ve started with budget remaining that aren’t fully scheduled, so nothing slips through the cracks. A good Thursday/Friday habit: each card has a Schedule button to slot the remaining hours.',
  },
  {
    id: 'gs-niche', roles: ['all'], route: '/schedule/employee/:me?tab=profile',
    target: '[data-tour="niche-prefs"]', fallback: '.sched-emp-panel',
    title: 'Set your niche preferences',
    body: 'Everyone, please do this: rank the niches you want to work in. They feed Staff Suggestions so you get matched to the work you care about. Not a commitment — change them anytime.',
  },
  {
    id: 'gs-suggestions', roles: ['all'], route: `/schedule/suggestions/${SENTINEL_JOB}`,
    target: '.sched-sug-card', fallback: '.sched-sug-list',
    title: 'How Staff Suggestions work',
    body: 'When staffing a job, candidates are ranked on prior experience, niche expertise, and interest — with availability shown (this is a sample). Expand a card for the full breakdown. It’s a guide, not an auto-decision.',
  },
  {
    id: 'gs-myjobs', roles: ['manager'], route: '/schedule/dashboard?tab=myjobs',
    target: '.sched-mgr-filters', fallback: '.sched-tabs',
    title: 'My Jobs (if you lead engagements)',
    body: 'Jobs you’re Job Partner/Manager on, sorted by due date with budget vs scheduled hours. Filter by department, status, or due-date range. Seeing a conflict here is for awareness — the production team resolves scheduling conflicts.',
  },
];

// ── Grid deep-dive (comprehensive; drive-driven on seeded demo data) ─────────
const GRID_TOUR = [
  {
    id: 'grid-views', roles: ['all'], route: GRID,
    target: '.sched-grid-viewtoggle',
    title: 'People vs Jobs view',
    body: 'People view puts staff on the rows (great for balancing a person’s week). Jobs view puts jobs on the rows (great for staffing one engagement). Toggle anytime — your filters carry over.',
  },
  {
    id: 'grid-dept', roles: ['all'], route: GRID,
    target: '.sched-grid-dept-tabs', fallback: '.sched-grid-toolbar',
    title: 'Filter by department (multi-select)',
    body: 'Click one or more departments to see a team view — e.g. Audit + General together. The left column shows each person’s utilization over the visible weeks.',
  },
  {
    id: 'grid-groups', roles: ['all'], route: GRID,
    target: '.sched-grid-filter-btn', fallback: '.sched-grid-toolbar',
    title: 'Saved staff groups & My Homeroom',
    body: 'Build and save your own staff groups (e.g. a niche team) for one-click views. Homeroom leaders get a My Homeroom group automatically.',
  },
  {
    id: 'grid-legend', roles: ['all'], route: GRID,
    target: '[data-tour="grid-legend"]', fallback: '.sched-grid-toolbar',
    title: 'Color legend',
    body: 'The legend explains every color — PTO, holidays, over-capacity, custom assignments — most useful on the department-wide views.',
  },
  {
    id: 'grid-window', roles: ['all'], route: GRID,
    target: '.sched-grid-zoom', extraHoles: ['.sched-grid-nav'], fallback: '.sched-grid-toolbar',
    title: 'Choose your time window',
    body: 'Set the zoom to control how many weeks you see — 1 week, 5 weeks, or a custom range. The ‹ and › arrows jump back/forward a week at a time, and Today snaps the view back to the current week.',
  },
  {
    id: 'grid-block', roles: ['all'], route: GRID,
    target: '[data-tour="demo-block"]', fallback: '.sched-grid-root',
    title: 'Your scheduled work',
    body: 'Each block is a job you’re scheduled on (this is a sample). Its color is the department; hover for details. Click a block to open its detail panel — let’s look.',
  },
  {
    id: 'grid-entry-sidebar', roles: ['all'], route: GRID, drive: 'grid-entry-detail',
    target: '.sched-grid-detail', placement: 'side',
    extraHoles: ['[data-tour="demo-block"]'],
    pulse: ['.sched-grid-action-edit'],
    fallback: '[data-tour="demo-block"]',
    title: 'Entry details',
    body: 'Clicking a block (highlighted, left) opens this panel: the client/job, assigned staffer, dates, hours, any note, and the job’s budget progress — plus Edit, Quick Optimize, View in PE, Remove, and History. Next we’ll hit Edit Entry to change it.',
  },
  {
    id: 'grid-edit-modes', roles: ['all'], route: GRID, drive: 'grid-edit-existing',
    target: '[data-tour="dist-modes"]', extraHoles: ['[data-tour="entry-hours"]'], fallback: '.sched-mgr-modal',
    title: 'Editing an entry — distribution',
    body: 'Here’s that sample entry open for editing, with its hours already loaded. Enter your true total in the hours box; Total spreads it evenly across the week, while By Day lets you set each day.',
  },
  {
    id: 'grid-edit-optimize', roles: ['all'], route: GRID, drive: 'grid-edit-existing',
    target: '[data-tour="entry-optimize"]',
    extraHoles: ['[data-tour="entry-extend-before"]', '[data-tour="entry-extend-after"]'],
    fallback: '.sched-mgr-modal',
    title: 'Optimize & Extend',
    body: 'Optimize redistributes hours within each day’s capacity (it turns amber when there’s a conflict). Extend before/after lets the work overflow into an adjacent week when one week can’t hold it all.',
  },
  {
    id: 'grid-edit-beforeafter', roles: ['all'], route: GRID, drive: 'grid-edit-existing',
    target: '[data-tour="modal-before-after"]', placement: 'side', fallback: '.sched-mgr-modal',
    title: 'See the impact before you save',
    body: 'As you change hours or run Optimize/Extend, this Before/After view shows each selected staffer’s new per-day hours (a tab per person in multi-staff) and how their capacity shifts — flagging conflicts with holidays, PTO, and existing work. A quick visual gut-check of what your changes will do.',
  },
  {
    id: 'grid-edit-multi', roles: ['all'], route: GRID, drive: 'grid-new-entry',
    target: '[data-tour="entry-multistaff"]', fallback: '.sched-mgr-modal',
    title: 'Schedule several people at once',
    body: 'On a new entry, switch to multi-staff to add several people to one job in a single go — each with their own distribution, with “Optimize all” across everyone. Ideal for big jobs like the EBP team.',
  },
  {
    id: 'grid-entry-placeholder', roles: ['all'], route: GRID, drive: 'grid-new-placeholder',
    target: '[data-tour="entry-placeholder-mode"]', placement: 'side', fallback: '.sched-mgr-modal',
    title: 'Reserve work with a placeholder',
    body: 'Don’t know exactly who yet? Anyone can choose “Assign placeholder to dept + level” instead of a person — it holds the hours on the job by department and level until someone is assigned later.',
  },
  {
    id: 'grid-suggest', roles: ['all'], route: GRID, drive: 'grid-modal-suggest',
    target: '[data-tour="modal-staff-picker"]', placement: 'side', fallback: '.sched-mgr-modal',
    title: 'Suggested staff for the job',
    body: 'Pick a job and the modal ranks candidates right here — scored on prior experience, niche & interest, with free hours for the week. Click one to assign without leaving the modal.',
  },
  {
    id: 'grid-fullanalysis', roles: ['all'], route: GRID, drive: 'grid-modal-suggest',
    target: '.sched-modal-fullanalysis-link', fallback: '.sched-mgr-modal',
    title: 'View detailed staff suggestions',
    body: 'Want the whole ranked list with filters and week-by-week availability? “View detailed staff suggestions” opens the complete Staff Suggestions page for this job.',
  },
  {
    id: 'grid-job-sidebar', roles: ['all'], route: GRID, drive: 'grid-job-detail',
    target: '.sched-grid-detail', placement: 'side',
    extraHoles: ['[data-tour="demo-job-row"]'],
    fallback: '.sched-grid-root',
    title: 'Jobs view — job details',
    body: 'In Jobs view, click a job on the left (highlighted) to open this panel: everyone staffed on it and their hours, the per-job budget progress, and the on-site toggle (which tailors staff suggestions to the office).',
  },
  {
    id: 'grid-drag', roles: ['all'], route: GRID, drive: 'grid-drag-demo',
    target: '.sched-mgr-modal', placement: 'side', fallback: '.sched-grid-root',
    title: 'Drag to move or reassign',
    body: 'In either People or Jobs view, drag a block to another day — or onto another person’s row — and you get this preview first. You can Optimize here to clear any capacity conflicts before confirming, and the same Before/After view lets you review the move before anything commits.',
  },
  {
    id: 'grid-placeholder-block', roles: ['super'], route: GRID, drive: 'grid-placeholder-block',
    target: '[data-tour="demo-placeholder"]', fallback: '.sched-grid-root',
    title: 'Placeholders flag unassigned work',
    body: 'In Jobs view, a placeholder (highlighted) reserves work on a job by department + level before a specific person is assigned. Click one to staff it — let’s open it.',
  },
  {
    id: 'grid-placeholder-assign', roles: ['super'], route: GRID, drive: 'grid-placeholder',
    target: '.sched-mgr-modal', placement: 'side', fallback: '.sched-grid-root',
    title: 'Assign the best-fit staffer',
    body: 'Opening a placeholder ranks candidates — scored on prior experience, niche & interest, with availability — so you can assign the best fit in one step.',
  },
];

// ── My Jobs deep-dive ───────────────────────────────────────────────────────
const MYJOBS_TOUR = [
  {
    id: 'mj-tab', roles: ['all'], route: '/schedule/dashboard?tab=myjobs',
    target: '.sched-mgr-filters', fallback: '.sched-tabs',
    title: 'My Jobs',
    body: 'Every job you’re Job Partner/Manager on, sorted by due date with a progress bar (charged + scheduled vs budget; no bar = no budget yet). Filter by department, status, or due-date range.',
  },
  {
    id: 'mj-sidebar', roles: ['all'], route: `/schedule/dashboard?tab=myjobs&job=${SENTINEL_JOB}`, drive: 'mgr-select-job',
    target: '.sched-mgr-sidebar', placement: 'side',
    extraHoles: [`[data-job-id="${SENTINEL_JOB}"]`],
    fallback: '.sched-mgr-filters',
    title: 'Job details',
    body: 'Clicking a job (we’ve scrolled to + highlighted the sample in the table) opens this panel: client, due date, budget progress, everyone staffed on it, and quick actions — Schedule Staff, Roll Forward, View Staff Suggestions, Link Job.',
  },
  {
    id: 'mj-suggestions', roles: ['all'], route: `/schedule/dashboard?tab=myjobs&job=${SENTINEL_JOB}`, drive: 'mgr-select-job',
    target: '.sched-mgr-suggestions-btn', placement: 'side', fallback: '.sched-mgr-sidebar',
    title: 'Find staff for a job',
    body: '“View Staff Suggestions” opens the ranked candidate list for this job — the same experience/niche/interest scoring used everywhere in the app.',
  },
  {
    id: 'mj-rollforward', roles: ['manager'], route: `/schedule/dashboard?tab=myjobs&job=${SENTINEL_JOB}`, drive: 'mgr-rollforward',
    target: '.sched-mgr-modal', placement: 'side', fallback: '.sched-mgr-sidebar',
    title: 'Roll Forward last year’s team',
    body: 'For recurring jobs, roll last year’s team forward in one step. Check who to bring, tweak each person’s hours / weeks / offset; departed staff become placeholders (pick a level for them).',
  },
  {
    id: 'mj-rf-preview', roles: ['manager'], route: `/schedule/dashboard?tab=myjobs&job=${SENTINEL_JOB}`, drive: 'mgr-rollforward-preview',
    target: '.sched-mgr-modal', placement: 'side', fallback: '.sched-mgr-sidebar',
    title: 'Preview the timeline',
    body: 'Preview shows the proposed schedule as a Gantt against the deadline — real staff as solid bars, placeholders dashed. Confirm & Schedule creates it all at once.',
  },
];

// ── Profile deep-dive ───────────────────────────────────────────────────────
const PROFILE_TOUR = [
  {
    id: 'pf-exp', roles: ['all'], route: '/schedule/employee/:me?tab=experience',
    target: '.sched-emp-exp-tabs', fallback: '.sched-emp-panel',
    title: 'Your experience',
    body: 'Every client you’ve worked on, your hours, and your percentile among all staff. Switch between Client, Niche, and Service Line breakdowns here — this is what powers Staff Suggestions.',
  },
  {
    id: 'pf-capacity', roles: ['all'], route: '/schedule/employee/:me?tab=capacity',
    target: '[data-tour="capacity-default"]', fallback: '.sched-emp-panel',
    title: 'Capacity',
    body: 'Your weekly available hours come from your PE work profile via your default calendar template (busy-season templates raise it in season). This is the capacity every scheduling view checks against.',
  },
  {
    id: 'pf-pto', roles: ['all'], route: '/schedule/employee/:me?tab=pto',
    target: '[data-tour="emp-tab-pto"]', fallback: '.sched-tabs',
    title: 'PTO & time off',
    body: 'PTO lives on its own tab — your approved and pending time off. Scheduling and Staff Suggestions account for it automatically so you’re not booked while you’re out.',
  },
  {
    id: 'pf-niche', roles: ['all'], route: '/schedule/employee/:me?tab=profile',
    target: '[data-tour="niche-prefs"]', fallback: '.sched-emp-panel',
    title: 'Niche preferences',
    body: 'Rank up to five niches you want to work in, then Save. These feed Staff Suggestions as a tiebreaker — keep them current.',
  },
];

const RAW_TRACKS = {
  gettingStarted: { key: 'gettingStarted', label: 'Getting Started (2 min)', steps: GETTING_STARTED },
  grid:    { key: 'grid',    label: 'Scheduler Grid in depth', steps: GRID_TOUR },
  myjobs:  { key: 'myjobs',  label: 'My Jobs (for managers)', steps: MYJOBS_TOUR },
  profile: { key: 'profile', label: 'Your Profile & Suggestions', steps: PROFILE_TOUR },
};

// pathname → deep-dive track key (for the chooser's "Tour this page").
export const ROUTE_TO_TRACK = [
  { match: (p) => p.startsWith('/schedule/grid'), key: 'grid' },
  { match: (p) => p.startsWith('/schedule/dashboard'), key: 'myjobs' },
  { match: (p) => p.startsWith('/schedule/employee'), key: 'profile' },
];

export function trackForPath(pathname) {
  const hit = ROUTE_TO_TRACK.find((r) => r.match(pathname || ''));
  return hit ? hit.key : null;
}

// Build role-filtered, route-resolved tracks for this user.
export function buildTracks({ staffId = null, isSuperUser = false } = {}) {
  // 'manager' steps show to everyone (anyone may lead jobs; demo-mode populates
  // them); only 'super' steps are gated. Tighten later with a real leads-jobs flag.
  const userRoles = isSuperUser ? ['all', 'manager', 'super'] : ['all', 'manager'];
  const canSee = (step) => (step.roles || ['all']).some((r) => userRoles.includes(r));

  const resolveRoute = (route) => {
    if (!route) return route;
    if (route.includes(':me')) {
      if (staffId == null) return null; // can't resolve → step dropped
      return route.replace(':me', String(staffId));
    }
    return route;
  };

  const out = {};
  for (const [k, track] of Object.entries(RAW_TRACKS)) {
    const steps = track.steps
      .filter(canSee)
      .map((s) => ({ ...s, route: resolveRoute(s.route) }))
      .filter((s) => s.route !== null); // drop steps whose :me route couldn't resolve
    out[k] = { ...track, steps };
  }
  return out;
}
